import { useCallback, useMemo, useState } from 'react';
import type { FormInstance } from 'antd/es/form';
import { useStripe } from '@stripe/react-stripe-js';
import { useDispatch, useSelector } from 'react-redux';

import { FunctionalRootState, Toaster } from '@share/utils';
import type { IAccount } from '@share/common-types';

import type { IFormErrors } from '@common-types';
import { ScrollBehaviorEnum } from '@common-types';

import type { CertificatePaymentFormProps, PaymentFormValues } from '../types';

import type { StripeCardElementsGroup } from '../components/stripe-card-fields';
import { ZERO } from '../constants';

import { runCertificatePurchase } from './certificate-purchase-submit';
import { runPublicCertificatePayment } from './public-certificate-payment-submit';
import type { PaymentFormRefs } from './usePaymentFormRefs';
import type { IntlShape } from 'react-intl';
import { buildCertificatePurchaseMessages } from './certificate-purchase-messages';
import type { CertificatePaymentFormVariant } from '../types';
import { registerPublicClubSiteMemberSession } from '../utils/public-clubsite-checkout-signup';

interface UseCertificatePurchaseSubmissionParams {
  intl: IntlShape;
  form: FormInstance<PaymentFormValues>;
  certificateTemplate: CertificatePaymentFormProps['certificateTemplate'];
  returnUrl: string;
  organizationId: number | undefined;
  certificateTotal: number;
  onSuccess: CertificatePaymentFormProps['onSuccess'];
  variant?: CertificatePaymentFormVariant;
  paymentToken?: string;
  requiresInlineSignup?: boolean;
  refs: Pick<PaymentFormRefs, 'wrapperRef' | 'card1GroupRef' | 'card2GroupRef'>;
}

export interface CertificatePurchaseSubmission {
  submitting: boolean;
  handleSubmit: () => void;
  setCard1Group: (group: StripeCardElementsGroup | null) => void;
  setCard2Group: (group: StripeCardElementsGroup | null) => void;
}

export function useCertificatePurchaseSubmission({
  intl,
  form,
  certificateTemplate,
  returnUrl,
  organizationId,
  certificateTotal,
  onSuccess,
  variant = 'template',
  paymentToken,
  requiresInlineSignup = false,
  refs,
}: UseCertificatePurchaseSubmissionParams): CertificatePurchaseSubmission {
  const stripe = useStripe();
  const dispatch = useDispatch();
  const { account } = useSelector((state: FunctionalRootState) => state.loginStore);
  const [submitting, setSubmitting] = useState(false);
  const { wrapperRef, card1GroupRef, card2GroupRef } = refs;
  const purchaseMessages = useMemo(() => buildCertificatePurchaseMessages(intl), [intl]);
  const successToast = intl.formatMessage({ id: 'certificates.purchase.successToast' });
  const publicPaymentUnavailableMessage = intl.formatMessage({
    id: 'certificates.publicPayment.linkExpired',
  });
  const signupFailedMessage = intl.formatMessage({
    id: 'certificates.payment.signupFailed',
  });
  const signupEmailInUseMessage = intl.formatMessage({
    id: 'certificates.payment.signupEmailInUse',
  });

  const handlePurchase = useCallback(
    async (values: PaymentFormValues) => {
      if (!stripe || !certificateTemplate) {
        return;
      }

      if (variant === 'public' && !paymentToken) {
        Toaster.error(publicPaymentUnavailableMessage);
        return;
      }

      setSubmitting(true);

      try {
        if (requiresInlineSignup) {
          if (!account) {
            Toaster.error(signupFailedMessage);
            return;
          }

          const signupResult = await registerPublicClubSiteMemberSession({
            dispatch,
            account: account as IAccount,
            values,
            signupFailedMessage,
            emailInUseMessage: signupEmailInUseMessage,
          });

          if (!signupResult.ok) {
            Toaster.error(signupResult.message);
            return;
          }
        }

        const outcome =
          variant === 'public'
            ? await runPublicCertificatePayment({
                stripe,
                certificateTemplate,
                returnUrl,
                paymentToken: paymentToken as string,
                certificateTotal,
                values,
                card1GroupRef,
                card2GroupRef,
                messages: purchaseMessages,
              })
            : await runCertificatePurchase({
                stripe,
                certificateTemplate,
                returnUrl,
                organizationId,
                certificateTotal,
                values,
                card1GroupRef,
                card2GroupRef,
                messages: purchaseMessages,
              });

        if (outcome.status === 'success') {
          Toaster.success(successToast);
          onSuccess(outcome.result, {
            paymentMode: outcome.paymentMode,
            ...(outcome.instalmentPlanMonths !== undefined
              ? { instalmentPlanMonths: outcome.instalmentPlanMonths }
              : {}),
          });
          return;
        }

        Toaster.error(outcome.message);
      } catch (err: unknown) {
        const message =
          err instanceof Error && err.message ? err.message : String(err);
        Toaster.error(message);
      } finally {
        setSubmitting(false);
      }
    },
    [
      stripe,
      certificateTemplate,
      returnUrl,
      onSuccess,
      organizationId,
      certificateTotal,
      card1GroupRef,
      card2GroupRef,
      purchaseMessages,
      successToast,
      variant,
      paymentToken,
      publicPaymentUnavailableMessage,
      requiresInlineSignup,
      account,
      dispatch,
      signupFailedMessage,
      signupEmailInUseMessage,
    ],
  );

  const handleSubmit = useCallback(() => {
    form
      .validateFields()
      .then((values: PaymentFormValues) => {
        handlePurchase(values);
      })
      .catch((error: IFormErrors) => {
        const errorFields = error?.errorFields;
        if (errorFields?.length && errorFields[ZERO]?.name?.length) {
          const fieldName = errorFields[ZERO].name[ZERO];
          const formInstance = form as unknown as {
            scrollToField: (name: string, opts: { behavior: string }) => void;
          };
          if (typeof formInstance.scrollToField === 'function') {
            formInstance.scrollToField(fieldName, { behavior: ScrollBehaviorEnum.Smooth });
          } else {
            wrapperRef.current?.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });
  }, [form, handlePurchase, wrapperRef]);

  const setCard1Group = useCallback(
    (group: StripeCardElementsGroup | null) => {
      card1GroupRef.current = group;
    },
    [card1GroupRef],
  );

  const setCard2Group = useCallback(
    (group: StripeCardElementsGroup | null) => {
      card2GroupRef.current = group;
    },
    [card2GroupRef],
  );

  return { submitting, handleSubmit, setCard1Group, setCard2Group };
}
